import React from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const OrdersGraph = ({ orders }) => {
  const safeOrders = Array.isArray(orders) ? orders : [];

  // Orders by status
  const statusCounts = safeOrders.reduce((acc, order) => {
    const status = order.status || "unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Orders per day
  const dateCounts = safeOrders.reduce((acc, order) => {
    const date = order.date_created
      ? new Date(order.date_created).toLocaleDateString()
      : "Unknown";
    acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});

  // Revenue by status
  const revenueByStatus = safeOrders.reduce((acc, order) => {
    const status = order.status || "unknown";
    acc[status] = (acc[status] || 0) + parseFloat(order.total || 0);
    return acc;
  }, {});

  // Today's stats
  const todayDate = new Date().toLocaleDateString();
  const ordersToday = safeOrders.filter(order =>
    new Date(order.date_created).toLocaleDateString() === todayDate
  );
  const totalOrdersToday = ordersToday.length;
  const totalRevenueToday = ordersToday.reduce(
    (sum, order) => sum + parseFloat(order.total || 0),
    0
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "30px", marginTop: "30px" }}>
      
      {/* Today's summary */}
      <div style={{ display: "flex", justifyContent: "space-around", background: "#f1f1f1", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <div style={{ textAlign: "center" }}>
          <h3 style={{ margin: 0, color: "#3498db" }}>Orders Today</h3>
          <p style={{ fontSize: "24px", fontWeight: "bold" }}>{totalOrdersToday}</p>
        </div>
        <div style={{ textAlign: "center" }}>
          <h3 style={{ margin: 0, color: "#27ae60" }}>Revenue Today (AED)</h3>
          <p style={{ fontSize: "24px", fontWeight: "bold" }}>{totalRevenueToday.toFixed(2)}</p>
        </div>
      </div>

      {/* Charts */}
      <div style={{ display: "flex", gap: "40px", flexWrap: "wrap" }}>
        {/* Doughnut: Orders by Status */}
        <div style={{ flex: "1 1 400px", minWidth: "350px", background: "#f9f9f9", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          <Doughnut
            data={{
              labels: Object.keys(statusCounts),
              datasets: [
                {
                  label: "Orders by Status",
                  data: Object.values(statusCounts),
                  backgroundColor: ["#f39c12", "#3498db", "#2ecc71", "#e74c3c", "#9b59b6", "#16a085"],
                  borderWidth: 2,
                  borderColor: "#fff",
                  hoverOffset: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { position: "right" },
                title: { display: true, text: "Orders by Status" },
              },
            }}
          />
        </div>

        {/* Line: Orders per Day */}
        <div style={{ flex: "2 1 600px", minWidth: "400px", background: "#f9f9f9", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          <Line
            data={{
              labels: Object.keys(dateCounts),
              datasets: [
                {
                  label: "Orders per Day",
                  data: Object.values(dateCounts),
                  borderColor: "#3498db",
                  backgroundColor: "rgba(52,152,219,0.2)",
                  tension: 0.4,
                  fill: true,
                  pointRadius: 6,
                  pointHoverRadius: 8,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { position: "top" },
                title: { display: true, text: "Orders by Date" },
              },
              scales: {
                x: { ticks: { autoSkip: false } },
                y: { beginAtZero: true, stepSize: 1 },
              },
            }}
          />
        </div>
      </div>

      {/* Bottom row: Revenue by Status */}
      <div style={{ minWidth: "400px", background: "#f9f9f9", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Bar
          data={{
            labels: Object.keys(revenueByStatus),
            datasets: [
              {
                label: "Revenue by Status ($)",
                data: Object.values(revenueByStatus),
                backgroundColor: ["#f1c40f", "#2980b9", "#27ae60", "#c0392b", "#8e44ad", "#16a085"],
                borderRadius: 8,
              },
            ],
          }}
          options={{
            responsive: true,
            plugins: {
              legend: { position: "top" },
              title: { display: true, text: "Revenue by Order Status" },
            },
            scales: { y: { beginAtZero: true } },
          }}
        />
      </div>
    </div>
  );
};

export default OrdersGraph;
